import React, { useEffect, useMemo, useState } from "react";
import { Input, Select } from "antd";
import moment from "moment";

const DOB_MONTH_OPTIONS = moment.months().map((month, index) => ({
  label: month,
  value: String(index + 1).padStart(2, "0"),
}));

const DOB_YEAR_MIN = 1900;
const DOB_YEAR_MAX = moment().year();

const DobInput = ({ value, onChange, disabled, onValidityChange }) => {
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");

  useEffect(() => {
    if (value && moment.isMoment(value) && value.isValid()) {
      setYear(value.format("YYYY"));
      setMonth(value.format("MM"));
      setDay(value.format("DD"));
      return;
    }

    setYear("");
    setMonth("");
    setDay("");
  }, [value]);

  const daysInMonth = useMemo(() => {
    const monthNumber = Number(month);
    const yearNumber = Number(year);

    if (!monthNumber) {
      return 31;
    }

    const safeYear =
      yearNumber >= DOB_YEAR_MIN && yearNumber <= DOB_YEAR_MAX
        ? yearNumber
        : 2000;

    return moment(
      `${safeYear}-${String(monthNumber).padStart(2, "0")}-01`,
    ).daysInMonth();
  }, [month, year]);

  const dayOptions = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, index) => ({
        label: String(index + 1),
        value: String(index + 1).padStart(2, "0"),
      })),
    [daysInMonth],
  );

  const emitValue = (nextMonth, nextDay, nextYear) => {
    if (!nextMonth || !nextDay || !nextYear || nextYear.length !== 4) {
      onChange?.(null);
      onValidityChange?.(false);
      return;
    }

    const nextMoment = moment(
      `${nextYear}-${nextMonth}-${nextDay}`,
      "YYYY-MM-DD",
      true,
    );

    if (
      !nextMoment.isValid() ||
      nextMoment.year() < DOB_YEAR_MIN ||
      nextMoment.year() > DOB_YEAR_MAX ||
      nextMoment.isAfter(moment(), "day")
    ) {
      onChange?.(null);
      onValidityChange?.(false);
      return;
    }

    onChange?.(nextMoment);
    onValidityChange?.(true);
  };

  const handleMonthChange = (nextMonth) => {
    setMonth(nextMonth || "");
    emitValue(nextMonth || "", day, year);
  };

  const handleDayChange = (nextDay) => {
    setDay(nextDay || "");
    emitValue(month, nextDay || "", year);
  };

  const handleYearChange = (event) => {
    const nextYear = event.target.value.replace(/\D/g, "").slice(0, 4);
    setYear(nextYear);
    emitValue(month, day, nextYear);
  };

  return (
    <div className="kycDobRow">
      <Input
        size="large"
        placeholder="Year"
        value={year}
        onChange={handleYearChange}
        disabled={disabled}
        inputMode="numeric"
        maxLength={4}
        className="kycDobField kycDobYear"
      />
      <Select
        size="large"
        placeholder="Month"
        value={month || undefined}
        options={DOB_MONTH_OPTIONS}
        onChange={handleMonthChange}
        disabled={disabled}
        className="kycDobField kycDobMonth"
      />
      <Select
        size="large"
        placeholder="Day"
        value={day || undefined}
        options={dayOptions}
        onChange={handleDayChange}
        disabled={disabled}
        className="kycDobField kycDobDay"
      />
    </div>
  );
};

export default DobInput;
